/**
 * Turns a movement preview into the sentences the reschedule dialog shows: where each named task lands, whether it adds a trip and which visits could take the work
 * instead, the compliance window, approval and booking effects, and what the server refuses. Pure, so the
 * wording is tested without mounting the dialog.
 */
import { VisitMoveApprovalEffect, VisitMoveBookingEffect, VisitMoveOutcomeClass, VisitMovePreview, VisitMoveRefusalCode, VisitMoveTaskPreview, VisitTaskWindowPlacement } from '../visit.movement.types';
import { VISIT_SEPARATE_REASON_LABELS } from './visit.move.target';

/** One line of the preview; `tone` says whether it needs the person's attention or blocks the move. */
export interface VisitMovePreviewLine {
  text: string;
  tone: 'neutral' | 'warning' | 'error';
}

/** The dialog's preview panel content. */
export interface VisitMovePreviewSummary {
  /** Where each task lands, one line per task, e.g. "Floor cleaning joins the visit already on 4 Nov". */
  outcomes: VisitMovePreviewLine[];
  /** Extra trip, window, approval, booking and Job consequences, then refusals. */
  notes: VisitMovePreviewLine[];
  /** Nothing can move: submit would be refused. */
  blocked: boolean;
}

/** What the dialog adds to the server's preview: wording it derives itself. */
export interface VisitMovePreviewContext {
  /** The client-side spacing warning; the server never refuses it, so it is a warning only. */
  spacing?: string;
  /** Upcoming visits the work could join instead, as the target picker names them. */
  joinable?: readonly string[];
}

/** `dateLabel` names the destination day ("4 Nov"). */
export function summariseVisitMovePreview(preview: VisitMovePreview, dateLabel: string, context: VisitMovePreviewContext = {}): VisitMovePreviewSummary {

  const tasks = preview.tasks;
  const outcomes = tasks.map(task => outcomeLine(task, dateLabel));
  const separate = tasks.some(task => task.outcome === VisitMoveOutcomeClass.SameDaySeparate);
  const creates = tasks.some(task => task.outcome === VisitMoveOutcomeClass.CreatesVisit);
  const joinable = context.joinable ?? [];
  const instead = joinable.length ? ` It could join ${orList(joinable)} instead.` : '';

  const notes: VisitMovePreviewLine[] = [];
  if (separate) notes.push({ text: `This adds a trip: a visit of this line is already on ${dateLabel} but cannot take this work.${instead}`, tone: 'warning' });
  else if (creates && joinable.length) notes.push({ text: `This adds a visit on ${dateLabel}.${instead}`, tone: 'warning' });
  const window = windowNote(tasks);
  if (window) notes.push(window);
  if (context.spacing) notes.push({ text: `${context.spacing}.`, tone: 'warning' });
  const needsReview = tasks.filter(task => task.approval === VisitMoveApprovalEffect.NeedsReview);
  if (needsReview.length) notes.push({ text: `Approved, will need approval again: ${taskNames(needsReview)}.`, tone: 'warning' });
  const approves = tasks.filter(task => task.approval === VisitMoveApprovalEffect.Approves);
  if (approves.length) notes.push({ text: `Approved at the new date: ${taskNames(approves)}.`, tone: 'neutral' });
  const restored = tasks.filter(task => task.approval === VisitMoveApprovalEffect.Restored);
  if (restored.length) notes.push({ text: `Back where approved, approval kept: ${taskNames(restored)}.`, tone: 'neutral' });
  if (tasks.some(task => task.booking === VisitMoveBookingEffect.Moved)) notes.push({ text: 'The booking moves with the work; the crew stays.', tone: 'neutral' });
  for (const warning of preview.warnings) notes.push({ text: warning.message, tone: 'warning' });
  for (const refusal of preview.refusals) notes.push({ text: refusal.message, tone: refusal.code === VisitMoveRefusalCode.OutsideWindow ? 'warning' : 'error' });

  return {
    outcomes,
    notes,
    blocked: !tasks.length || preview.refusals.some(refusal => !refusal.visitTaskId),
  };
}

function outcomeLine(task: VisitMoveTaskPreview, dateLabel: string): VisitMovePreviewLine {

  const name = taskName(task);
  switch (task.outcome) {
    case VisitMoveOutcomeClass.JoinsVisit: return { text: `${name} joins the visit already on ${dateLabel}`, tone: 'neutral' };
    case VisitMoveOutcomeClass.CreatesVisit: return { text: `${name} goes to a new visit on ${dateLabel}`, tone: 'neutral' };
    default: return { text: `${name} goes to a separate visit (${separateReasons(task)})`, tone: 'warning' };
  }
}

/** An older server answers without names; the task is still counted, never dropped. */
function taskName(task: VisitMoveTaskPreview): string {

  return task.name?.trim() || 'A task';
}

function taskNames(tasks: VisitMoveTaskPreview[]): string {

  return tasks.map(taskName).join(', ');
}

/** "a, b or c". */
function orList(labels: readonly string[]): string {

  if (labels.length === 1) return labels[0];

  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

function separateReasons(task: VisitMoveTaskPreview): string {

  const reasons = task.separateReasons ?? [];
  if (!reasons.length) return 'cannot share a visit';

  return reasons.map(reason => VISIT_SEPARATE_REASON_LABELS[reason]).join(', ');
}

/** The worst placement wins: a breach outranks an early service. */
function windowNote(tasks: VisitMoveTaskPreview[]): VisitMovePreviewLine | null {

  const placements = new Set(tasks.map(task => task.window));
  if (placements.has(VisitTaskWindowPlacement.NoCommonWindow)) return { text: 'These tasks share no compliance window; no single date suits all of them.', tone: 'warning' };
  if (placements.has(VisitTaskWindowPlacement.Late)) return { text: 'Late: after the compliance window closes, so this is a compliance breach.', tone: 'warning' };
  if (placements.has(VisitTaskWindowPlacement.Early)) return { text: 'Early: before the compliance window opens, so the service will not count.', tone: 'warning' };

  return null;
}
